import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, AlertTriangle } from "lucide-react";
import { Input } from "./ui/input";
import { api, errMsg } from "../lib/api";

const COUNTRIES = ["AT", "DE", "CH", "IT", "CZ", "SK", "HU", "SI", "HR", "NL", "BE", "FR", "ES", "PL"];
const FUZZY = [/^erste bank und sparkassen$/i, /erste/i, /sparkasse/i, /george/i];

export const pickDefault = (banks) => {
  for (const re of FUZZY) { const b = banks.find((x) => re.test(x.name)); if (b) return b; }
  return null;
};

export const BankPicker = ({ s, onChange }) => {
  const [banks, setBanks] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const country = s.aspsp_country || "AT";
  useEffect(() => {
    setBanks(null); setErr("");
    api.bankAspsps(country).then((list) => {
      setBanks(list);
      if (!s.aspsp_name || !list.some((b) => b.name === s.aspsp_name)) {
        const d = pickDefault(list);
        if (d) onChange({ aspsp_name: d.name, psu_type: d.psu_types.includes("business") ? "business" : d.psu_types[0] });
      }
    }).catch((e) => { setBanks([]); setErr(errMsg(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);
  const shown = useMemo(() => (banks || []).filter((b) => b.name.toLowerCase().includes(q.toLowerCase())), [banks, q]);
  const sel = (banks || []).find((b) => b.name === s.aspsp_name);
  return (
    <div className="grid gap-3" data-testid="bank-picker">
      <div className="flex flex-wrap items-end gap-3">
        <label className="grid gap-1 text-sm font-semibold text-slate-700">Land
          <select value={country} onChange={(e) => onChange({ aspsp_country: e.target.value, aspsp_name: null })} className="h-10 rounded-md border border-input bg-white px-2 text-sm" data-testid="bank-country-select">
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="grid flex-1 gap-1 text-sm font-semibold text-slate-700">Bank suchen
          <div className="relative"><Search className="absolute left-2.5 top-3 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="z. B. Erste, Sparkasse" className="h-10 pl-8" data-testid="bank-search-input" /></div>
        </label>
      </div>
      {banks === null && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Bankliste wird von Enable Banking geladen…</div>}
      {err && <p className="flex items-start gap-1.5 rounded-lg bg-red-50 p-3 text-sm text-red-800" data-testid="bank-aspsp-error"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{err}</p>}
      {banks?.length > 0 && (
        <ul className="max-h-56 overflow-y-auto rounded-lg border border-slate-200" data-testid="bank-aspsp-list">
          {shown.map((b) => (
            <li key={b.name}>
              <button type="button" onClick={() => onChange({ aspsp_name: b.name, psu_type: b.psu_types.includes("business") ? "business" : b.psu_types[0] })}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${b.name === s.aspsp_name ? "bg-slate-100 font-semibold" : ""}`} data-testid={`bank-option-${b.name.replace(/\W+/g, "-")}`}>
                {b.logo ? <img src={b.logo} alt="" className="h-6 w-6 rounded object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="h-6 w-6 rounded bg-slate-200" />}
                <span className="flex-1">{b.name}</span>
                <span className="text-xs text-slate-500">{b.psu_types.join(" / ")}{b.max_days ? ` · max. ${b.max_days} Tage` : ""}</span>
              </button>
            </li>
          ))}
          {!shown.length && <li className="px-3 py-2 text-sm text-slate-500">Keine Bank gefunden.</li>}
        </ul>
      )}
      {banks?.length === 0 && !err && <p className="text-sm text-amber-800" data-testid="bank-aspsp-empty">Enable Banking liefert für {country} keine Banken. Bei einer „restricted production“-App erscheinen nur die Banken der im Control Panel verknüpften Konten.</p>}
      {sel && (
        <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="bank-selected">
          Ausgewählt: <b>{sel.name}</b>
          {sel.psu_types.length > 1 ? (
            <select value={s.psu_type || "business"} onChange={(e) => onChange({ psu_type: e.target.value })} className="h-9 rounded-md border border-input bg-white px-2" data-testid="bank-psu-select">
              <option value="business">Geschäftskonto (business)</option><option value="personal">Privatkonto (personal)</option>
            </select>
          ) : <span className="text-slate-500">({sel.psu_types[0]})</span>}
        </div>
      )}
    </div>
  );
};
